// ---------------------------------------------------------------------------
// Interaction Prompt — small bottom-center overlay that shows
// "[E] OPEN DOOR / CLIMB LADDER / PICK UP <weapon> / DEFUSE / PLANT" hints
// when the player is within interaction range of a relevant target.
//
// The prompt is single-string, mutually exclusive: priorities are bomb >
// pickup > door > ladder. Callers compute the desired state per frame and
// pass it through `setInteractionPrompt`. Calling with `null` hides it.
//
// This intentionally renders as a plain DOM overlay (no Three.js cost) so
// the prompt stays sharp at any UI scale and can be hidden cheaply.
// ---------------------------------------------------------------------------

import { isMobile } from '../core/device';

let promptEl: HTMLDivElement | null = null;
let contentEl: HTMLDivElement | null = null;
let holdTrackEl: HTMLDivElement | null = null;
let holdFillEl: HTMLDivElement | null = null;
let holdProgressTimer: ReturnType<typeof setInterval> | null = null;
let lastText: string | null = null;

function ensureElement(): HTMLDivElement {
  if (promptEl) return promptEl;
  const el = document.createElement('div');
  const mobile = isMobile();
  el.id = 'interaction-prompt';
  el.style.cssText =
    (mobile
      ? 'position:fixed;left:auto;right:20%;bottom:40%;transform:none;'
      : 'position:fixed;left:50%;bottom:22%;transform:translateX(-50%);') +
    'padding:6px 14px;font-family:"Inter","Segoe UI",sans-serif;' +
    'font-size:clamp(13px, 2.2vw, 16px);letter-spacing:0.04em;color:#f0f0f0;' +
    'text-align:center;line-height:1.2;' +
    'background:rgba(0,0,0,0.55);border:1px solid rgba(255,255,255,0.18);' +
    'border-radius:4px;pointer-events:none;z-index:120;' +
    'opacity:0;transition:opacity 80ms ease-out;overflow:hidden;';

  const content = document.createElement('div');
  content.style.position = 'relative';
  content.style.zIndex = '1';
  el.appendChild(content);

  const track = document.createElement('div');
  track.style.cssText =
    'position:absolute;left:0;right:0;bottom:0;height:2px;' +
    'background:rgba(255,255,255,0.12);display:none;overflow:hidden;';

  const fill = document.createElement('div');
  fill.style.cssText =
    'width:0%;height:100%;background:#e8b849;' +
    'box-shadow:0 0 6px rgba(232,184,73,0.9);';
  track.appendChild(fill);
  el.appendChild(track);

  document.body.appendChild(el);
  promptEl = el;
  contentEl = content;
  holdTrackEl = track;
  holdFillEl = fill;
  return el;
}

function renderPrompt(el: HTMLDivElement, text: string, secondaryText?: string): void {
  const content = contentEl ?? el;
  if (!secondaryText) {
    content.textContent = text;
    return;
  }

  content.textContent = '';

  const primary = document.createElement('div');
  primary.textContent = text;
  content.appendChild(primary);

  const secondary = document.createElement('div');
  secondary.textContent = secondaryText;
  secondary.style.fontSize = '11px';
  secondary.style.color = 'rgba(240,240,240,0.72)';
  secondary.style.letterSpacing = '0.02em';
  secondary.style.marginTop = '3px';
  content.appendChild(secondary);
}

function clearHoldProgressTimer(): void {
  if (holdProgressTimer === null) return;
  clearInterval(holdProgressTimer);
  holdProgressTimer = null;
}

/**
 * Show or update the interaction prompt. Pass `null` to hide.
 * `key` defaults to `E` — pass an alternate label (e.g. `F`) for non-default
 * keybinds; the caller can read `keybindManager.getKeyForAction('interact')`
 * if it wants to surface a remapped key.
 */
export function setInteractionPrompt(action: string | null, key = 'E', secondaryText?: string): void {
  const text = action ? `[${key}] ${action}` : null;
  const stateText = text ? `${text}\n${secondaryText ?? ''}` : null;
  if (stateText === lastText) return;
  lastText = stateText;
  const el = ensureElement();
  if (text) {
    renderPrompt(el, text, secondaryText);
    el.style.opacity = '1';
  } else {
    el.style.opacity = '0';
  }
}

/** Update the hold-to-interact progress bar. Pass null to hide and reset it. */
export function setHoldProgress(startedAt: number | null, durationMs: number): void {
  const el = ensureElement();
  void el;
  clearHoldProgressTimer();

  if (startedAt === null || durationMs <= 0) {
    if (holdTrackEl) holdTrackEl.style.display = 'none';
    if (holdFillEl) holdFillEl.style.width = '0%';
    return;
  }

  if (holdTrackEl) holdTrackEl.style.display = 'block';
  const updateProgress = (): void => {
    const elapsed = Date.now() - startedAt;
    const progress = Math.max(0, Math.min(1, elapsed / durationMs));
    if (holdFillEl) holdFillEl.style.width = `${Math.round(progress * 100)}%`;
    if (progress >= 1) clearHoldProgressTimer();
  };

  updateProgress();
  holdProgressTimer = setInterval(updateProgress, 50);
}

/** Remove the overlay and reset state — call on game.dispose(). */
export function disposeInteractionPrompt(): void {
  clearHoldProgressTimer();
  if (promptEl) {
    promptEl.remove();
    promptEl = null;
  }
  contentEl = null;
  holdTrackEl = null;
  holdFillEl = null;
  lastText = null;
}
